<template>
  <div v-if="availableTabs.length > 1" class="content-selector__navbar flex items-center gap-2 mt-3">
    <CButton
      v-for="t in availableTabs"
      :key="t.type"
      secondary
      :type="t.type === activeSelectorType ? 'primary' : 'default'"
      @click="onTypeSelect(t.type)"
    >
      {{ t.text }}
    </CButton>
  </div>
</template>

<script setup lang="ts">
import { computed, type ComputedRef, inject, type ToRefs, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import type { SelectorType } from '@content-selector/stores/app'
import { useStore } from '@content-selector/composables/useStore'
import type { AppProps } from '@content-selector/appTypes'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { useMultimediaSelectorApps } from '@content-selector/composables/useMultimediaSelectorApps'

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const appProps = inject<ToRefs<AppProps>>('appProps')
const { t } = useI18n()
const availableSelectorTypes: SelectorType[] = ['content', 'assets', 'external_links']
if (launchDarkly.value.enable_multimedia_component_ai_images) {
  availableSelectorTypes.push('ai_images')
}
// Always include Pexels images
availableSelectorTypes.push('pexels_images')

if (launchDarkly.value.enable_themes && launchDarkly.value.enable_theme_assets) {
  availableSelectorTypes.push('theme')
}

const texts = {
  content: t('canvasUI.contentSelectorApp.navbar.content'),
  assets: t('canvasUI.contentSelectorApp.navbar.assets'),
  external_links: t('canvasUI.contentSelectorApp.navbar.externalLinks'),
  ai_images: t('canvasUI.contentSelectorApp.navbar.aiImages'),
  pexels_images: t('canvasUI.contentSelectorApp.navbar.pexelsImages'),
  theme: t('canvasUI.contentSelectorApp.navbar.theme'),
} as const

// Load multimedia selector apps
const { multimediaSelectorApps, loadMultimediaSelectorApps } = useMultimediaSelectorApps()

onMounted(async () => {
  await loadMultimediaSelectorApps()
})
const availableTabs = computed(() => {
  const baseTabs = availableSelectorTypes
    .filter((t) =>
      appProps?.allowedSelectorTypes?.value?.length ? appProps.allowedSelectorTypes.value.includes(t) : true,
    )
    .map((type) => ({
      type,
      text: type in texts ? texts[type as keyof typeof texts] : type,
    }))

  // Add tabs for each multimedia-selector app
  const multimediaSelectorTabs = appProps?.allowMultimediaSelectorApp?.value
    ? multimediaSelectorApps.value.map((app) => ({
        type: `multimedia-selector-${app.id}` as SelectorType,
        text: app.app_metadata?.display_name || app.name,
      }))
    : []

  return [...baseTabs, ...multimediaSelectorTabs]
})
const { activeSelectorType, setActiveSelectorType } = useStore()

function onTypeSelect(type: SelectorType) {
  setActiveSelectorType(type)
}
</script>
